import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Button from './ui/Button';
import NumericInput from './NumericInput';
import { ConfirmModal } from './ui/ConfirmModal';
import PaymentQRModal from './PaymentQRModal';
import './MemberPayments.css';
import { translateError } from '../utils/errorMessages';

interface OutstandingItem {
  type: 'event' | 'membership';
  id: number;
  name: string;
  amount: number;
  paidAmount: number;
  balance: number;
  dueDate?: string;
  durationDays?: number;
}

interface PaymentItem {
  _id: number;
  member: { _id: number; name: string } | null;
  event: { _id: number; name: string; startTime: string } | null;
  membership: { _id: number; name: string; yearlyPrice: number | null } | null;
  date: string;
  amount: number;
  description: string;
}

interface MemberPaymentsProps {
  memberId: string;
  memberName: string;
  refreshKey?: number;
}

const MemberPayments: React.FC<MemberPaymentsProps> = ({ memberId, memberName, refreshKey }) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'outstanding' | 'paid'>('outstanding');

  // Outstanding state
  const [outstanding, setOutstanding] = useState<OutstandingItem[]>([]);
  const [outstandingLoading, setOutstandingLoading] = useState(true);

  // Paid state
  const [paidPayments, setPaidPayments] = useState<PaymentItem[]>([]);
  const [paidLoading, setPaidLoading] = useState(false);

  // QR modal
  const [qrModal, setQrModal] = useState<{
    isOpen: boolean;
    type: 'event' | 'membership';
    entityId: number;
    entityName: string;
    amount: number;
    durationDays?: number;
  }>({ isOpen: false, type: 'event', entityId: 0, entityName: '', amount: 0 });

  // Add payment form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', date: new Date().toISOString().split('T')[0], description: '' });
  const [addLoading, setAddLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ amount: '', date: '', description: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Refund state
  const [refundingPayment, setRefundingPayment] = useState<PaymentItem | null>(null);
  const [refundForm, setRefundForm] = useState({ amount: '', description: '' });
  const [refundLoading, setRefundLoading] = useState(false);

  const fetchOutstanding = useCallback(async () => {
    try {
      setOutstandingLoading(true);
      const { data } = await api.payments.getOutstanding(memberId);
      setOutstanding(data as OutstandingItem[]);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setOutstandingLoading(false);
    }
  }, [memberId]);

  const fetchPaid = useCallback(async () => {
    try {
      setPaidLoading(true);
      const { data } = await api.payments.list({ member: memberId });
      const response = data as { payments: PaymentItem[] };
      setPaidPayments(response.payments || []);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setPaidLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchOutstanding();
  }, [fetchOutstanding]);

  // Re-fetch when parent signals a change (e.g. tier change)
  useEffect(() => {
    if (refreshKey) {
      fetchOutstanding();
      if (activeTab === 'paid') fetchPaid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    if (activeTab === 'paid' && paidPayments.length === 0) {
      fetchPaid();
    }
  }, [activeTab, paidPayments.length, fetchPaid]);

  const openQRModal = (item: OutstandingItem) => {
    setQrModal({
      isOpen: true,
      type: item.type,
      entityId: item.id,
      entityName: item.name,
      amount: item.balance,
      durationDays: item.durationDays,
    });
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await api.payments.create({
        member: Number(memberId),
        amount: parseFloat(newPayment.amount),
        date: newPayment.date,
        description: newPayment.description,
      });
      setNewPayment({ amount: '', date: new Date().toISOString().split('T')[0], description: '' });
      setShowAddForm(false);
      await Promise.all([fetchOutstanding(), fetchPaid()]);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setAddLoading(false);
    }
  };

  const startEditing = (payment: PaymentItem) => {
    setEditingId(payment._id);
    setEditForm({
      amount: String(payment.amount),
      date: payment.date.split('T')[0],
      description: payment.description || '',
    });
  };

  const handleEditPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId === null) return;
    setEditLoading(true);
    try {
      await api.payments.update(String(editingId), {
        member: Number(memberId),
        amount: parseFloat(editForm.amount),
        date: editForm.date,
        description: editForm.description,
      });
      setEditingId(null);
      await Promise.all([fetchOutstanding(), fetchPaid()]);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeletePayment = async () => {
    if (deleteId === null) return;
    setDeleteLoading(true);
    try {
      await api.payments.delete(String(deleteId));
      setDeleteId(null);
      await Promise.all([fetchOutstanding(), fetchPaid()]);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setDeleteLoading(false);
    }
  };

  const startRefund = (payment: PaymentItem) => {
    setRefundingPayment(payment);
    setRefundForm({ amount: String(payment.amount), description: '' });
  };

  const handleRefund = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!refundingPayment) return;
    setRefundLoading(true);
    try {
      await api.payments.refund(String(refundingPayment._id), {
        amount: parseFloat(refundForm.amount),
        description: refundForm.description || undefined,
      });
      addToast(t('payments.refundSuccess'), 'success');
      setRefundingPayment(null);
      await Promise.all([fetchOutstanding(), fetchPaid()]);
    } catch (err: any) {
      addToast(translateError(err, t, 'payments.refundError'), 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="member-payments">
      {error && <div className="error-message">{error}</div>}

      <div className="payments-tabs">
        <button
          className={`payments-tab ${activeTab === 'outstanding' ? 'active' : ''}`}
          onClick={() => setActiveTab('outstanding')}
        >
          {t('payments.outstanding')}
          {outstanding.length > 0 && (
            <span className="tab-badge">{outstanding.length}</span>
          )}
        </button>
        <button
          className={`payments-tab ${activeTab === 'paid' ? 'active' : ''}`}
          onClick={() => setActiveTab('paid')}
        >
          {t('payments.paidHistory')}
        </button>
      </div>

      {activeTab === 'outstanding' && (
        <div className="outstanding-list">
          {outstandingLoading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : outstanding.length === 0 ? (
            <div className="no-items">{t('payments.noOutstanding')}</div>
          ) : (
            outstanding.map((item) => (
              <div key={`${item.type}-${item.id}`} className="outstanding-item">
                <div className="outstanding-info">
                  <span className={`outstanding-type-badge ${item.type}`}>
                    {item.type === 'event' ? t('common.event') : t('payments.membership')}
                  </span>
                  <span className="outstanding-name">{item.name}</span>
                  {item.dueDate && (
                    <span className="outstanding-date">{formatDate(item.dueDate)}</span>
                  )}
                </div>
                <div className="outstanding-amounts">
                  <span className="outstanding-balance">{item.balance.toLocaleString()}</span>
                  <Button
                    onClick={() => openQRModal(item)}
                    title={t('payments.generateQR')}
                  >
                    QR
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'paid' && (
        <div className="paid-list">
          <div className="paid-header">
            <Button onClick={() => setShowAddForm(!showAddForm)}>
              {showAddForm ? t('common.cancel') : t('payments.addPayment')}
            </Button>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddPayment} className="add-payment-form">
              <div className="form-row">
                <NumericInput
                  step="1"
                  min="0.01"
                  placeholder={t('payments.amount')}
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                  required
                />
                <input
                  type="date"
                  value={newPayment.date}
                  onChange={(e) => setNewPayment(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              <input
                type="text"
                placeholder={t('payments.description')}
                value={newPayment.description}
                onChange={(e) => setNewPayment(prev => ({ ...prev, description: e.target.value }))}
              />
              <Button type="submit" loading={addLoading}>
                {t('payments.addButton')}
              </Button>
            </form>
          )}

          {paidLoading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : paidPayments.length === 0 ? (
            <div className="no-items">{t('payments.noPayments')}</div>
          ) : (
            paidPayments.map((payment) =>
              editingId === payment._id ? (
                <form key={payment._id} onSubmit={handleEditPayment} className="add-payment-form">
                  <div className="form-row">
                    <NumericInput
                      step="1"
                      min="0.01"
                      placeholder={t('payments.amount')}
                      value={editForm.amount}
                      onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                      required
                    />
                    <input
                      type="date"
                      value={editForm.date}
                      onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                      required
                    />
                  </div>
                  <input
                    type="text"
                    placeholder={t('payments.description')}
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <div className="form-row">
                    <Button type="submit" loading={editLoading}>
                      {t('payments.savePayment')}
                    </Button>
                    <Button variant="secondary" onClick={() => setEditingId(null)} disabled={editLoading}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              ) : refundingPayment?._id === payment._id ? (
                <form key={payment._id} onSubmit={handleRefund} className="add-payment-form">
                  <div className="form-row">
                    <NumericInput
                      step="1"
                      min="0.01"
                      max={String(payment.amount)}
                      placeholder={t('payments.refundAmount')}
                      value={refundForm.amount}
                      onChange={(e) => setRefundForm(prev => ({ ...prev, amount: e.target.value }))}
                      required
                    />
                    <span className="refund-max-hint">
                      {t('payments.maxRefund')}: {payment.amount.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder={t('payments.refundDescription')}
                    value={refundForm.description}
                    onChange={(e) => setRefundForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <div className="form-row">
                    <Button type="submit" loading={refundLoading}>
                      {t('payments.refundConfirm')}
                    </Button>
                    <Button variant="secondary" onClick={() => setRefundingPayment(null)} disabled={refundLoading}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <div key={payment._id} className={`paid-item${payment.amount < 0 ? ' refund' : ''}`}>
                  <div className="paid-info">
                    <span className="paid-date">{formatDate(payment.date)}</span>
                    {payment.amount < 0 && (
                      <span className="refund-badge">{t('payments.refundOf')}</span>
                    )}
                    <span className="paid-description">
                      {payment.event?.name || payment.membership?.name || payment.description || '-'}
                    </span>
                  </div>
                  <div className="paid-item-actions">
                    <span className={`paid-amount${payment.amount < 0 ? ' refund' : ''}`}>
                      {payment.amount < 0 ? '\u2212' : ''}{Math.abs(payment.amount).toLocaleString()}
                    </span>
                    {payment.amount > 0 && hasPermission('payments:write') && (
                      <button
                        className="paid-action-btn refund"
                        onClick={() => startRefund(payment)}
                        title={t('payments.refund')}
                      >
                        &#8634;
                      </button>
                    )}
                    {hasPermission('payments:write') && (
                      <button
                        className="paid-action-btn edit"
                        onClick={() => startEditing(payment)}
                        title={t('payments.editPayment')}
                      >
                        &#9998;
                      </button>
                    )}
                    {hasPermission('payments:delete') && (
                      <button
                        className="paid-action-btn delete"
                        onClick={() => setDeleteId(payment._id)}
                        title={t('payments.deletePayment')}
                      >
                        &#128465;
                      </button>
                    )}
                  </div>
                </div>
              )
            )
          )}
        </div>
      )}

      <PaymentQRModal
        isOpen={qrModal.isOpen}
        onClose={() => setQrModal(prev => ({ ...prev, isOpen: false }))}
        type={qrModal.type}
        entityId={qrModal.entityId}
        entityName={qrModal.entityName}
        memberId={Number(memberId)}
        memberName={memberName}
        amount={qrModal.amount}
        durationDays={qrModal.durationDays}
      />

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeletePayment}
        title={t('payments.deletePaymentTitle')}
        message={t('payments.deletePaymentConfirm')}
        confirmText={t('payments.deletePayment')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default MemberPayments;
